import React from 'react';
import { useNavigate, Link } from 'react-router-dom';
import ProductCard from '../components/ProductCard';
import { useSeller } from '../context/SellerContext';
import { useCategories } from '../context/CategoryContext';
import { sanitizeHtml } from '../utils/sanitize';
import useProductFilters from '../hooks/useProductFilters';
import '../styles/common.css';


function ProductList({ addToCart }) {
  const {
    products, loading, error, fetchProducts,
    selectedCategory, setSelectedCategory,
    searchKeyword, categorySettings,
    suggestedProducts, suggestedLabel,
    currentPage, setCurrentPage, totalPages, totalItems,
    sortOption, setSortOption, priceFilters, setPriceFilters, priceRanges,
    handlePriceFilterChange, handleSortChange, handlePageChange,
  } = useProductFilters();
  const { isSellerSite } = useSeller();
  const { categories: ctxCategories, adminCategories } = useCategories();
  const navigate = useNavigate();

  // DB에서 조회한 카테고리 (displayOrder 순 정렬됨)
  const CATEGORIES = adminCategories.map(cat => ({ name: cat.name || cat.title, value: cat.categoryValue, hot: cat.isHot }));
  const SPECIAL_CATEGORIES = [
    { name: '베스트 상품', value: 'best' },
    { name: '신상품', value: 'new' },
    { name: '추천 상품', value: 'recommended' }
  ];


  // 카테고리 필터: 전체 + 주요 카테고리
  const categoryFilters = [
    { name: '전체', value: '전체' },
    ...SPECIAL_CATEGORIES.map(cat => ({ name: cat.name, value: cat.value })),
    ...CATEGORIES.filter(cat => !cat.hot).map(cat => ({ name: cat.name, value: cat.value }))
  ];


  // 현재 카테고리 정보 가져오기
  const getCurrentCategoryInfo = () => {
    const allCategories = [...CATEGORIES, ...SPECIAL_CATEGORIES];
    return allCategories.find(cat => cat.value === selectedCategory);
  };

  const currentCategoryInfo = getCurrentCategoryInfo();

  const handleCategoryChange = (e) => {
    const value = e.target.value;
    setSelectedCategory(value);
    navigate(`/products?category=${value}`);
    setCurrentPage(1);
  };



  // Helper to determine active state for sidebar (simple logic for now)
  const isCategoryActive = (catValue) => selectedCategory === catValue;

  return (
    <div className="flex flex-col min-h-screen">
      <main className="mx-auto w-full max-w-[1400px] px-6 lg:px-10 py-8 flex-1">
        {/* Breadcrumb */}
        <nav className="flex text-xs text-gray-400 mb-6 gap-2 items-center">
          <span className="material-symbols-outlined text-xs">home</span>
          <Link to="/">홈</Link>
          <span className="material-symbols-outlined text-[10px]">arrow_forward_ios</span>
          <span className="text-gray-600 dark:text-gray-200 font-medium">
            {searchKeyword ? `"${searchKeyword}" 검색 결과` : (currentCategoryInfo ? currentCategoryInfo.name : '전체 상품')}
          </span>
        </nav>

        <div className="flex gap-8">
          {/* Sidebar */}
          <aside className="hidden lg:block w-64 flex-shrink-0">
            <div className="sticky top-24 space-y-8">
              <div>
                <h3 className="text-sm font-bold mb-4 flex items-center gap-2">
                  <span className="material-symbols-outlined text-lg">filter_alt</span>
                  상세 검색 필터
                </h3>

                {/* Search Input */}
                <form
                  onSubmit={(e) => {
                    e.preventDefault();
                    const value = e.target.elements.sidebarSearch.value.trim();
                    if (value) {
                      navigate(`/products?search=${encodeURIComponent(value)}`);
                    } else {
                      navigate('/products');
                    }
                  }}
                  className="mb-6"
                >
                  <div className="relative">
                    <span className="absolute inset-y-0 left-0 flex items-center pl-3 text-gray-400">
                      <span className="material-symbols-outlined text-lg">search</span>
                    </span>
                    <input
                      name="sidebarSearch"
                      type="text"
                      defaultValue={searchKeyword}
                      key={searchKeyword}
                      className="w-full h-10 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-[#1d3324] pl-9 pr-3 text-sm placeholder:text-gray-400 focus:ring-2 focus:ring-primary focus:border-primary dark:text-white transition-all"
                      placeholder="상품명 검색"
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') e.target.form.requestSubmit();
                      }}
                    />
                  </div>
                </form>

                {/* Categories */}
                <div className="space-y-6">
                  <div>
                    <h4 className="text-xs font-bold text-gray-500 mb-3">카테고리</h4>
                    <div className="space-y-2">
                      <label className="flex items-center gap-2 cursor-pointer group">
                        <input 
                          type="radio" 
                          name="category"
                          className="rounded border-gray-300 text-primary focus:ring-primary"
                          checked={selectedCategory === '전체'}
                          onChange={() => {
                            setSelectedCategory('전체');
                            navigate('/products');
                          }}
                        />
                        <span className={`text-sm group-hover:text-primary-strong ${selectedCategory === '전체' ? 'text-primary-strong font-medium' : ''}`}>
                          전체 보기
                        </span>
                      </label>
                      {SPECIAL_CATEGORIES.map(cat => (
                        <label key={cat.value} className="flex items-center gap-2 cursor-pointer group">
                          <input 
                            type="radio" 
                            name="category"
                            className="rounded border-gray-300 text-primary focus:ring-primary"
                            checked={selectedCategory === cat.value}
                            onChange={() => {
                              setSelectedCategory(cat.value);
                              navigate(`/products?category=${cat.value}`);
                            }}
                          />
                          <span className={`text-sm group-hover:text-primary-strong ${selectedCategory === cat.value ? 'text-primary-strong font-medium' : ''}`}>
                            {cat.name}
                          </span>
                        </label>
                      ))}
                      {CATEGORIES.filter(cat => !cat.hot).map(cat => (
                        <label key={cat.value} className="flex items-center gap-2 cursor-pointer group">
                          <input 
                            type="radio" 
                            name="category"
                            className="rounded border-gray-300 text-primary focus:ring-primary"
                            checked={selectedCategory === cat.value}
                            onChange={() => {
                              setSelectedCategory(cat.value);
                              navigate(`/products?category=${cat.value}`);
                            }}
                          />
                          <span className={`text-sm group-hover:text-primary-strong ${selectedCategory === cat.value ? 'text-primary-strong font-medium' : ''}`}>
                            {cat.name}
                          </span>
                        </label>
                      ))}
                    </div>
                  </div>

                  {/* Visual-only filters based on design - NOW FUNCTIONAL PRICE */}
                  <div>
                    <h4 className="text-xs font-bold text-gray-500 mb-3">가격 범위</h4>
                    <div className="space-y-2">
                      {priceRanges.map((range) => (
                        <label key={range.value} className="flex items-center gap-2 cursor-pointer group">
                          <input 
                            type="checkbox" 
                            className="rounded border-gray-300 text-primary focus:ring-primary"
                            checked={priceFilters.includes(range.value)}
                            onChange={() => handlePriceFilterChange(range.value)}
                          />
                          <span className={`text-sm group-hover:text-primary-strong ${priceFilters.includes(range.value) ? 'text-primary-strong font-medium' : ''}`}>
                            {range.label}
                          </span>
                        </label>
                      ))}
                    </div>
                  </div>

                  <div>
                     <button 
                      onClick={() => {
                        setSelectedCategory('전체');
                        setPriceFilters([]);
                        setSortOption('popular');
                        setCurrentPage(1);
                        navigate('/products');
                      }}
                      className="w-full mt-8 py-3 bg-[#0d1b12] dark:bg-primary dark:text-primary-contrast text-white text-xs font-bold rounded-xl hover:opacity-90 transition-opacity"
                    >
                      필터 초기화
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </aside>

          {/* Main Content */}
          <div className="flex-1">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-8">
              <div>
                <h2 className="text-2xl font-extrabold flex items-center gap-2 text-[#0d1b12] dark:text-white">
                  {searchKeyword ? `"${searchKeyword}" 검색 결과` : (currentCategoryInfo ? currentCategoryInfo.name : '전체 상품')}
                  <span className="text-sm font-medium text-gray-400">
                    {!loading ? `총 ${totalItems}개의 상품` : '로딩 중...'}
                  </span>
                </h2>
                {categorySettings?.description && (
                  <p className="text-sm text-gray-500 mt-1" dangerouslySetInnerHTML={{ __html: sanitizeHtml(categorySettings.description) }} />
                )}
              </div>
              
              {/* Sort Options - Functionally connected */}
              <div className="flex items-center gap-2 overflow-x-auto pb-2 sm:pb-0">
                <button 
                  onClick={() => handleSortChange('popular')}
                  className={`flex-shrink-0 px-4 py-2 text-xs font-bold rounded-full transition-colors ${sortOption === 'popular' ? 'text-primary-strong bg-primary/10' : 'text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-800'}`}
                >
                  판매 인기순
                </button>
                <button 
                  onClick={() => handleSortChange('priceLow')}
                  className={`flex-shrink-0 px-4 py-2 text-xs font-bold rounded-full transition-colors ${sortOption === 'priceLow' ? 'text-primary-strong bg-primary/10' : 'text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-800'}`}
                >
                  낮은 가격순
                </button>
                <button 
                  onClick={() => handleSortChange('priceHigh')}
                  className={`flex-shrink-0 px-4 py-2 text-xs font-bold rounded-full transition-colors ${sortOption === 'priceHigh' ? 'text-primary-strong bg-primary/10' : 'text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-800'}`}
                >
                  높은 가격순
                </button>
                <button 
                  onClick={() => handleSortChange('new')}
                  className={`flex-shrink-0 px-4 py-2 text-xs font-bold rounded-full transition-colors ${sortOption === 'new' ? 'text-primary-strong bg-primary/10' : 'text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-800'}`}
                >
                  신상품순
                </button>
              </div>
            </div>

            {/* Mobile Search (shown only on small screens where sidebar is hidden) */}
            <form
              onSubmit={(e) => {
                e.preventDefault();
                const value = e.target.elements.mobileSearch.value.trim();
                if (value) {
                  navigate(`/products?search=${encodeURIComponent(value)}`);
                } else {
                  navigate('/products');
                }
              }}
              className="lg:hidden mb-6"
            >
              <div className="relative flex h-10">
                <span className="absolute inset-y-0 left-0 flex items-center pl-3 text-gray-400">
                  <span className="material-symbols-outlined text-lg">search</span>
                </span>
                <input
                  name="mobileSearch"
                  type="text"
                  defaultValue={searchKeyword}
                  key={searchKeyword}
                  className="w-full rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-[#1d3324] pl-9 pr-3 text-sm placeholder:text-gray-400 focus:ring-2 focus:ring-primary focus:border-primary dark:text-white transition-all"
                  placeholder="상품명 검색"
                />
              </div>
            </form>

            {loading && (
              <div className="flex justify-center items-center py-20">
                <div className="w-10 h-10 border-4 border-primary border-t-transparent rounded-full animate-spin"></div>
              </div>
            )}

            {error && (
              <div className="bg-red-50 text-red-500 p-4 rounded-xl text-center">
                <p>{error}</p>
                <button onClick={fetchProducts} className="mt-2 text-sm font-bold underline">다시 시도</button>
              </div>
            )}

            {!loading && !error && products.length === 0 && (
              <div>
                <div className="text-center py-12 text-gray-500 bg-gray-50 dark:bg-[#0d1b12]/50 rounded-2xl">
                  <span className="material-symbols-outlined text-4xl mb-2">inventory_2</span>
                  <p>{searchKeyword ? `"${searchKeyword}"에 대한 검색 결과가 없습니다.` : '표시할 상품이 없습니다.'}</p>
                </div>

                {suggestedProducts.length > 0 && (
                  <div className="mt-10">
                    <h3 className="text-lg font-bold text-[#0d1b12] dark:text-white mb-4 flex items-center gap-2">
                      <span className="material-symbols-outlined text-primary-strong" style={{ color: 'var(--primary-strong)' }}>
                        {suggestedLabel === '추천 상품' ? 'recommend' : 'schedule'}
                      </span>
                      {suggestedLabel}
                    </h3>
                    <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-6">
                      {suggestedProducts.map((product) => (
                        <ProductCard key={product.id} product={product} addToCart={addToCart} />
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}

            {!loading && !error && products.length > 0 && (
              <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-6">
                {products.map((product) => (
                  <ProductCard key={product.id} product={product} addToCart={addToCart} />
                ))}
              </div>
            )}

            {/* Pagination - Functional */}
            {!loading && totalPages > 1 && (
              <div className="mt-16 flex justify-center items-center gap-2">
                <button 
                  onClick={() => handlePageChange(currentPage - 1)}
                  disabled={currentPage === 1}
                  className="h-10 w-10 flex items-center justify-center rounded-lg border border-gray-200 dark:border-gray-800 hover:border-primary transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <span className="material-symbols-outlined text-sm">chevron_left</span>
                </button>
                
                {/* Dynamically generate page numbers (simplified: show max 5 pages or all) */}
                {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                   let pageNum = i + 1;
                   // Logic to center current page if many pages exist could go here, 
                   // for now showing 1-5 or 1-totalPages
                   if (totalPages > 5 && currentPage > 3) {
                     pageNum = currentPage - 2 + i;
                     if (pageNum > totalPages) pageNum = totalPages - (4 - i);
                   }
                   
                   return (
                    <button 
                      key={pageNum}
                      onClick={() => handlePageChange(pageNum)}
                      className={`h-10 w-10 flex items-center justify-center rounded-lg text-sm font-bold transition-all ${
                        currentPage === pageNum 
                          ? 'bg-primary text-primary-contrast' 
                          : 'border border-gray-200 dark:border-gray-800 hover:border-primary text-gray-600 dark:text-gray-300'
                      }`}
                    >
                      {pageNum}
                    </button>
                   );
                })}

                <button 
                  onClick={() => handlePageChange(currentPage + 1)}
                  disabled={currentPage === totalPages}
                  className="h-10 w-10 flex items-center justify-center rounded-lg border border-gray-200 dark:border-gray-800 hover:border-primary transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <span className="material-symbols-outlined text-sm">chevron_right</span>
                </button>
              </div>
            )}
          </div>
        </div>
      </main>
    </div>
  );
}

export default ProductList;

